import {
    useCallback,
    useEffect,
    useState,
} from "react";

import { toast } from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";


export default function SellerOrders() {
    const {
        t,
        lang,
    } = useLang();

    const [
        orders,
        setOrders,
    ] = useState([]);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        updatingOrderId,
        setUpdatingOrderId,
    ] = useState(null);


    const load = useCallback(async () => {
        try {
            const response =
                await api.get(
                    "/seller/orders"
                );

            setOrders(response.data);

        } catch (error) {
            toast.error(
                formatApiError(
                    error.response
                        ?.data
                        ?.detail
                )
                || (
                    lang === "ar"
                        ? "تعذر تحميل الطلبات"
                        : "Could not load orders"
                )
            );

        } finally {
            setLoading(false);
        }
    }, [lang]);


    useEffect(() => {
        load();
    }, [load]);


    const statusLabel = (status, order = null) => {
        if (
            order
            && (
                order.payment_status === "refunded"
                || order.refund_status === "refunded"
            )
        ) {
            return lang === "ar"
                ? "مسترجع"
                : "Refunded";
        }

        const labels = {

            pending_payment:
                lang === "ar"
        ? "بانتظار إتمام الدفع"
        : "Pending payment",

            confirmed:
                lang === "ar"
                    ? "طلب جديد"
                    : "New order",

            accepted:
                lang === "ar"
                    ? "تم قبول الطلب"
                    : "Accepted",

            preparing:
                lang === "ar"
                    ? "قيد التجهيز"
                    : "Preparing",

            shipped:
                lang === "ar"
                    ? "تم الشحن"
                    : "Shipped",

            delivered:
                lang === "ar"
                    ? "تم التوصيل"
                    : "Delivered",

            rejected:
                lang === "ar"
                    ? "مرفوض"
                    : "Rejected",

            cancelled:
                lang === "ar"
                    ? "\u0645\u0644\u063a\u064a"
                    : "Cancelled",
        };

        return (
            labels[status]
            || status
            || "-"
        );
    };


    const paymentStatusLabel = (
        paymentStatus
    ) => {
        const labels = {
            paid:
                lang === "ar"
                    ? "مدفوع"
                    : "Paid",

            pending:
                lang === "ar"
                    ? "بانتظار الدفع"
                    : "Payment pending",

            failed:
                lang === "ar"
                    ? "فشل الدفع"
                    : "Payment failed",

            refunded:
                lang === "ar"
                    ? "\u062a\u0645 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a"
                    : "Refunded",

            cancelled:
                lang === "ar"
                    ? "ملغي"
                    : "Cancelled",
        };

        return (
            labels[paymentStatus]
            || paymentStatus
            || "-"
        );
    };


    const paymentMethodLabel = (
        method
    ) => {
        if (
            method === "moyasar"
            || method === "card"
            || method === "online"
        ) {
            return (
                lang === "ar"
                    ? "دفع إلكتروني"
                    : "Online payment"
            );
        }

        if (
            method
            === "cash_on_delivery"
        ) {
            return (
                lang === "ar"
                    ? "الدفع عند الاستلام"
                    : "Cash on delivery"
            );
        }

        return method || "";
    };


    const getAvailableStatuses = (
    order
) => {

    if (
        order.payment_method === "moyasar"
        && order.payment_status !== "paid"
    ) {
        return [
            order.status
        ];
    }

    switch (order.status) {

        case "confirmed":

            // Paid Moyasar orders cannot
            // be rejected by seller.
            if (
                order.payment_method === "moyasar"
                && order.payment_status === "paid"
            ) {
                return [
                    "confirmed",
                    "accepted",
                ];
            }

            return [
                "confirmed",
                "accepted",
                "rejected",
            ];

        case "accepted":
            return [
                "accepted",
                "preparing",
            ];

        case "preparing":
            return [
                "preparing",
                "shipped",
            ];

        case "shipped":
            return [
                "shipped",
                "delivered",
            ];

        case "delivered":
            return [
                "delivered",
            ];

        case "rejected":
            return [
                "rejected",
            ];

        case "pending_payment":
            return [
                "pending_payment",
            ];

        default:
            return [
                order.status
            ].filter(Boolean);
    }
};


    const setStatus = async (
        orderId,
        newStatus
    ) => {
        const order =
            orders.find(
                (item) =>
                    item.order_id
                    === orderId
            );

        if (
            !order
            || order.status
                === newStatus
        ) {
            return;
        }

        setUpdatingOrderId(
            orderId
        );

        try {
            await api.post(
                `/seller/orders/${orderId}/status`,
                {
                    status:
                        newStatus,
                }
            );

            toast.success(
                lang === "ar"
                    ? "تم تحديث حالة الطلب"
                    : "Order status updated"
            );

            await load();

        } catch (error) {
            toast.error(
                formatApiError(
                    error.response
                        ?.data
                        ?.detail
                )
                || (
                    lang === "ar"
                        ? "تعذر تحديث حالة الطلب"
                        : "Could not update order status"
                )
            );

        } finally {
            setUpdatingOrderId(
                null
            );
        }
    };


    const formatDate = (
        dateValue
    ) => {
        if (!dateValue) {
            return "-";
        }

        try {
            return new Date(
                dateValue
            ).toLocaleDateString(
                lang === "ar"
                    ? "ar-SA"
                    : "en-US",
                {
                    year:
                        "numeric",
                    month:
                        "2-digit",
                    day:
                        "2-digit",
                }
            );

        } catch {
            return dateValue;
        }
    };


    if (loading) {
        return (
            <Layout>
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-20 text-center">

                    <p className="text-muted-foreground">
                        {lang === "ar"
                            ? "جاري تحميل الطلبات..."
                            : "Loading orders..."
                        }
                    </p>

                </div>
            </Layout>
        );
    }


    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">

                <h1 className="text-3xl md:text-4xl font-black text-espresso">
                    {t("orders")}
                </h1>


                <div
                    className="mt-8 space-y-4"
                    data-testid="seller-orders-list"
                >

                    {orders.length === 0 ? (

                        <div className="text-muted-foreground text-center py-16">
                            {t("noResults")}
                        </div>

                    ) : (

                        orders.map((order) => {

                            const availableStatuses =
                                getAvailableStatuses(
                                    order
                                );

                            const paymentLocked =
                            order.payment_method === "moyasar"
                            && order.payment_status !== "paid";
                            const isFinal =
                            order.status === "delivered"
                        || order.status === "rejected"
                        || paymentLocked;
                               
                                
                                

                            return (
                                <div
                                    key={
                                        order.order_id
                                    }
                                    className="bg-white border border-border rounded-2xl p-5 md:p-6"
                                >

                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">

                                        <div className="space-y-2">

                                            <div className="flex flex-wrap items-center gap-2">

                                                <div className="text-xs text-muted-foreground">
                                                    #
                                                    {
                                                        order.order_id
                                                    }
                                                </div>


                                                <span
                                                    className={
                                                        `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${
                                                            order.payment_status
                                                            === "paid"
                                                                ? "bg-green-100 text-green-700"
                                                                : order.payment_status
                                                                === "failed"
                                                                || order.payment_status
                                                                === "cancelled"
                                                                    ? "bg-red-100 text-red-700"
                                                                    : "bg-amber-100 text-amber-700"
                                                        }`
                                                    }
                                                >
                                                    {
                                                        paymentStatusLabel(
                                                            order.payment_status
                                                        )
                                                    }
                                                </span>


                                                {order.payment_method && (
                                                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                                                        {
                                                            paymentMethodLabel(
                                                                order.payment_method
                                                            )
                                                        }
                                                    </span>
                                                )}

                                            </div>


                                            <div className="font-bold text-espresso">
                                                {
                                                    formatDate(
                                                        order.created_at
                                                    )
                                                }
                                            </div>


                                            <div className="text-sm text-muted-foreground">
                                                {
                                                    order.address
                                                }
                                                {" · "}
                                                {
                                                    order.phone
                                                }
                                            </div>


                                            {order.city && (
                                                <div className="text-xs text-muted-foreground">
                                                    {lang === "ar"
                                                        ? "المدينة: "
                                                        : "City: "
                                                    }
                                                    {
                                                        order.city
                                                    }
                                                </div>
                                            )}


                                            {order.notes && (
                                                <div className="text-xs text-muted-foreground">
                                                    {lang === "ar"
                                                        ? "ملاحظات: "
                                                        : "Notes: "
                                                    }

                                                    {
                                                        order.notes
                                                    }
                                                </div>
                                            )}

                                        </div>


                                        <div className="flex flex-wrap items-center gap-3">

                                            <Select
                                                value={
                                                    order.status
                                                }
                                                disabled={
                                                    isFinal
                                                    || updatingOrderId
                                                        === order.order_id
                                                }
                                                onValueChange={
                                                    (
                                                        value
                                                    ) =>
                                                        setStatus(
                                                            order.order_id,
                                                            value
                                                        )
                                                }
                                            >

                                                <SelectTrigger
                                                    className="w-44"
                                                    data-testid={
                                                        `status-select-${order.order_id}`
                                                    }
                                                >
                                                    <SelectValue>
                                                        {
                                                            statusLabel(
                                                                order.status,
                                                                order
                                                            )
                                                        }
                                                    </SelectValue>
                                                </SelectTrigger>


                                                <SelectContent>

                                                    {
                                                        availableStatuses.map(
                                                            (
                                                                status
                                                            ) => (
                                                                <SelectItem
                                                                    key={
                                                                        status
                                                                    }
                                                                    value={
                                                                        status
                                                                    }
                                                                >
                                                                    {
                                                                        statusLabel(
                                                                            status
                                                                        )
                                                                    }
                                                                </SelectItem>
                                                            )
                                                        )
                                                    }

                                                </SelectContent>

                                            </Select>


                                            <div className="text-end whitespace-nowrap space-y-1">

                                                <div className="text-sm text-muted-foreground">
                                                    {
                                                        lang === "ar"
                                                            ? "\u0645\u0628\u0644\u063a \u0628\u064a\u0639\u0643"
                                                            : "Your sales"
                                                    }
                                                    {": "}
                                                    <span className="font-bold text-espresso">
                                                        {
                                                            Number(
                                                                order.seller_products_total
                                                                || 0
                                                            ).toFixed(2)
                                                        }
                                                        {" "}
                                                        {lang === "ar"
                                                            ? "\u0631.\u0633"
                                                            : "SAR"
                                                        }
                                                    </span>
                                                </div>

                                                <div className="text-sm text-muted-foreground">
                                                    {
                                                        lang === "ar"
                                                            ? "\u0631\u0633\u0648\u0645 \u0627\u0644\u062a\u0648\u0635\u064a\u0644"
                                                            : "Delivery fee"
                                                    }
                                                    {": "}
                                                    <span className="font-bold text-espresso">
                                                        {
                                                            Number(
                                                                order.seller_shipping_amount
                                                                || 0
                                                            ).toFixed(2)
                                                        }
                                                        {" "}
                                                        {lang === "ar"
                                                            ? "\u0631.\u0633"
                                                            : "SAR"
                                                        }
                                                    </span>
                                                </div>

                                                <div className="text-xl font-black text-terracotta">
                                                    {
                                                        lang === "ar"
                                                            ? "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a"
                                                            : "Total"
                                                    }
                                                    {": "}
                                                    {
                                                        Number(
                                                            order.seller_order_total
                                                            || 0
                                                        ).toFixed(2)
                                                    }
                                                    {" "}
                                                    {lang === "ar"
                                                        ? "\u0631.\u0633"
                                                        : "SAR"
                                                    }
                                                </div>

                                            </div>

                                        </div>

                                    </div>


                                    <div className="mt-5 border-t border-border pt-4">

                                        <div className="text-sm font-bold text-espresso mb-3">
                                            {lang === "ar"
                                                ? "المنتجات"
                                                : "Products"
                                            }
                                        </div>


                                        <div className="space-y-2">

                                            {
                                                (
                                                    order.items
                                                    || []
                                                ).map(
                                                    (
                                                        item,
                                                        index
                                                    ) => (
                                                        <div
                                                            key={
                                                                `${item.product_id}-${index}`
                                                            }
                                                            className="flex justify-between gap-4 text-sm"
                                                        >

                                                            <span className="text-muted-foreground">
                                                                {
                                                                    item.title
                                                                }
                                                                {" × "}
                                                                {
                                                                    item.quantity
                                                                }
                                                            </span>


                                                            <span className="font-bold text-espresso whitespace-nowrap">
                                                                {
                                                                    Number(
                                                                        item.line_total
                                                                        || (
                                                                            Number(
                                                                                item.price
                                                                                || 0
                                                                            )
                                                                            * Number(
                                                                                item.quantity
                                                                                || 0
                                                                            )
                                                                        )
                                                                    ).toFixed(
                                                                        2
                                                                    )
                                                                }
                                                                {" "}
                                                                {lang === "ar"
                                                                    ? "ر.س"
                                                                    : "SAR"
                                                                }
                                                            </span>

                                                        </div>
                                                    )
                                                )
                                            }

                                        </div>

                                    </div>


                                    <div className="mt-4 text-xs text-muted-foreground">

                                        {lang === "ar"
                                            ? "حالة الطلب الحالية: "
                                            : "Current order status: "
                                        }

                                        <span className="font-bold">
                                            {
                                                statusLabel(
                                                    order.status,
                                                    order
                                                )
                                            }
                                        </span>

                                    </div>

                                </div>
                            );
                        })

                    )}

                </div>

            </div>
        </Layout>
    );
}